import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { memo } from 'react';

import { ResponsiveValue, getResponsiveStyles } from '@skippay/core/styles/responsivity';
import { space } from '@skippay/core/styles/spacing';

export type FlexProps = {
	variant: ResponsiveValue<'row' | 'column' | 'row-reverse' | 'column-reverse'>;
	gap?: ResponsiveValue<number>;
	align?: ResponsiveValue<'start' | 'center' | 'end' | 'stretch' | 'baseline'>;
	justify?: ResponsiveValue<'around' | 'between' | 'stretch' | 'start' | 'center' | 'end'>;
	wrap?: ResponsiveValue<'wrap' | 'wrap-reverse' | 'nowrap'>;
	display?: ResponsiveValue<'default' | 'inline'>;
	grow?: 1 | 2 | 3 | 4 | 5 | 6;
};

export const Flex = memo(
	styled.div<FlexProps>`
		display: flex;

		${({ align }) =>
			getResponsiveStyles(align ?? 'none', {
				start: { alignItems: 'flex-start' },
				center: { alignItems: 'center' },
				end: { alignItems: 'flex-end' },
				stretch: { alignItems: 'stretch' },
				baseline: { alignItems: 'baseline' },
				none: {},
			})}

		${({ justify }) =>
			getResponsiveStyles(justify ?? 'none', {
				around: { justifyContent: 'space-around' },
				between: { justifyContent: 'space-between' },
				start: { justifyContent: 'flex-start' },
				center: { justifyContent: 'center' },
				end: { justifyContent: 'flex-end' },
				stretch: { justifyContent: 'stretch' },
				none: {},
			})}

  		${({ wrap }) =>
			getResponsiveStyles(wrap ?? 'nowrap', {
				wrap: { flexWrap: 'wrap' },
				'wrap-reverse': { flexWrap: 'wrap-reverse' },
				nowrap: { flexWrap: 'nowrap' },
			})}

  		${({ display }) =>
			getResponsiveStyles(display ?? 'default', {
				default: { display: 'flex' },
				inline: { display: 'inline-flex' },
			})}

  		${({ variant, gap }) =>
			getResponsiveStyles(variant ?? 'row', {
				row: css`
					flex-direction: row;
					${getResponsiveStyles(gap, (number) => (number != null ? space(number, 0) : {}))}
				`,
				column: css`
					flex-direction: column;
					${getResponsiveStyles(gap, (number) => (number != null ? space(0, number) : {}))}
				`,
				'row-reverse': css`
					flex-direction: row-reverse;
					${getResponsiveStyles(gap, (number) => (number != null ? space(0, number) : {}))}
				`,
				'column-reverse': css`
					flex-direction: column-reverse;
					${getResponsiveStyles(gap, (number) => (number != null ? space(0, number) : {}))}
				`,
			})}
		${({ grow }) =>
			grow &&
			css`
				flex-grow: ${grow};
			`}
	`,
	(prevProps, nextProps) => {
		const nonObjectProps = Object.keys(prevProps).filter(
			(key) => typeof prevProps[key] !== 'object' || key === 'children'
		);
		const nonObjectNextProps = Object.keys(nextProps).filter(
			(key) => typeof nextProps[key] !== 'object' || key === 'children'
		);

		return (
			nonObjectProps.length === nonObjectNextProps.length &&
			nonObjectProps.every((key) => prevProps[key] === nextProps[key])
		);
	}
);

Flex.displayName = 'Flex';
